import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import Button from '@ui/components/Button/Button';
import Card from '@ui/components/Card/Card';
import Content from '@ui/components/Content/Content';
import styles from '@ui/components/Accordion/Accordion.module.scss';
import Heading from '@ui/components/Heading/Heading';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  useDisclosureStore,
  Disclosure,
  DisclosureContent,
} from '@ariakit/react';
import React, { forwardRef, useState } from 'react';
import { getHeadingTag } from '../../utils';

// easily extendable if in the future more variants should come
const findElement = variant => {
  switch (variant) {
    case 'card':
      return Card;
    default:
      return 'div';
  }
};

const Accordion = forwardRef(
  (
    {
      accordionState,
      area,
      callback,
      children,
      className,
      disclosureSize = 'default',
      headingLevel = 2,
      index,
      isOpen = false,
      subtitle,
      title,
      variant,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(isOpen);
    const Element = findElement(variant);

    const disclosure = useDisclosureStore({
      open,
      setOpen,
    });

    const isVisible =
      accordionState && !accordionState?.allowMultiple
        ? index === accordionState?.selectedIndex
        : open;

    const handleToggle = () => {
      if (accordionState && !accordionState?.allowMultiple) {
        if (!isVisible) {
          accordionState?.setSelectedIndex(index);
        } else {
          accordionState?.setSelectedIndex(-1);
        }
        return;
      }

      disclosure?.toggle();

      if (callback) {
        callback(isVisible);
      }
    };

    return (
      <Element
        className={classNames(
          styles.accordion,
          { [styles[variant]]: variant },
          className,
        )}
        ref={ref}
        {...props}
      >
        <Disclosure
          className={classNames(styles.header, { [styles[variant]]: variant })}
          store={disclosure}
          toggleOnClick={handleToggle}
        >
          {area ? (
            <div className={styles.areaHeadingContainer}>
              {!!area && (
                <Content aria-label="Bereich" as="span" className={styles.area}>
                  {area}
                </Content>
              )}
              <Heading
                as={getHeadingTag(headingLevel)}
                className={styles.heading}
                noSpacing
                size="h6"
              >
                {title}
              </Heading>
            </div>
          ) : (
            <Heading
              as={getHeadingTag(headingLevel)}
              className={styles.heading}
              noSpacing
              size="h6"
            >
              {title}
            </Heading>
          )}
          {!!subtitle && <span className={styles.subtitle}>{subtitle}</span>}
          <Button
            aria-hidden
            className={styles.button}
            render={
              <div>
                <FontAwesomeProIcon
                  className={classNames(styles.arrow, {
                    [styles.open]: isVisible,
                  })}
                  icon="chevron-down"
                  role="presentation"
                />
              </div>
            }
            role="presentation"
            shape="bubble"
            size={disclosureSize}
            tabIndex={-1}
          />
        </Disclosure>
        <DisclosureContent
          alwaysVisible={isVisible}
          className={classNames(styles.content, { [styles[variant]]: variant })}
          role="region"
          store={disclosure}
        >
          {children}
        </DisclosureContent>
      </Element>
    );
  },
);

Accordion.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  accordionState: PropTypes.object,
  area: PropTypes.string,
  callback: PropTypes.func,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  disclosureSize: PropTypes.oneOf(['small', 'default']),
  headingLevel: PropTypes.number,
  index: PropTypes.number,
  isOpen: PropTypes.bool,
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(['card']),
};

Accordion.displayName = 'Accordion';

export default Accordion;
